import { graphql } from '@/__generated__';
import { useCacheMonitor } from '@/context/DiscoveryUrqlProvider';
import { usePaginatedQuery } from '@/reducers/usePaginatedQuery';

type Result = ReturnType<typeof useLatestIssues>;

export type LatestIssuesResponse = NonNullable<Result['data']>[number];

export const GetLatestIssues = graphql(/* GraphQL */ `
  query LatestIssues($environmentId: BigInt!, $first: Int, $after: String) {
    environment(id: $environmentId) {
      applied {
        lastUpdatedAt
        latestIssues(first: $first, after: $after) {
          totalCount
          pageInfo {
            endCursor
            hasNextPage
          }
          edges {
            node {
              issueType
              runId
              status
              uniqueId
              resource {
                fqn
                filePath
                resourceType
                name
                ... on SourceAppliedStateNestedNode {
                  sourceExec: freshness {
                    runDate: freshnessRunGeneratedAt
                  }
                }
                ... on TestAppliedStateNestedNode {
                  testExec: executionInfo {
                    runDate: runGeneratedAt
                  }
                }
                ... on ModelAppliedStateNestedNode {
                  access
                  modelExec: executionInfo {
                    runDate: runGeneratedAt
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`);

type LatestIssuesParams = {
  pageSize: number;
  environmentId: number | undefined;
  pause?: boolean;
};

export const useLatestIssues = ({
  pageSize,
  environmentId = 0,
  pause,
}: LatestIssuesParams) => {
  const query = usePaginatedQuery(
    {
      query: GetLatestIssues,
      variables: {
        first: pageSize,
        after: undefined,
        environmentId,
      },
      pause: pause || !environmentId,
    },
    (data) => {
      const latestIssues = data.environment.applied?.latestIssues;
      const pageInfo = latestIssues?.pageInfo;
      if (latestIssues == null || pageInfo == null) return undefined;
      return {
        totalCount: latestIssues.totalCount ?? null,
        endCursor: pageInfo.endCursor ?? null,
        hasNextPage: pageInfo.hasNextPage ?? false,
      };
    },
    (data) => data.environment.applied?.latestIssues?.edges.map((edge) => edge.node),
    (data) => data.environment.applied?.lastUpdatedAt,
  );

  useCacheMonitor({ definition: query.lastUpdatedAt });

  return query;
};
